/**
 * Log Redaction Utility
 *
 * Provides secure logging by automatically redacting sensitive data
 * such as API keys, tokens, secrets, and passwords.
 */

// ============================================================
// Constants
// ============================================================

/**
 * Keys that should be redacted when logging objects
 */
const SENSITIVE_KEYS: readonly string[] = [
  'apiKey',
  'api_key',
  'apikey',
  'x-api-key',
  'token',
  'access_token',
  'refresh_token',
  'secret',
  'password',
  'passwd',
  'credential',
  'authorization',
  'auth',
  'bearer',
  'private_key',
  'privateKey',
] as const;

/**
 * Redaction placeholder for completely hidden values
 */
const REDACTED = '[REDACTED]';

// ============================================================
// Core Functions
// ============================================================

/**
 * Masks a sensitive value, showing only first and last 4 characters
 * for debugging purposes while hiding the middle portion.
 *
 * @param value - The sensitive value to mask
 * @param showChars - Number of characters to show at start and end (default: 4)
 * @returns Masked string like "abcd...efgh"
 *
 * @example
 * maskSecret('sk-1234567890abcdef') // 'sk-1...cdef'
 * maskSecret('short') // '[REDACTED]' (too short to safely mask)
 */
export function maskSecret(value: string, showChars: number = 4): string {
  if (!value || typeof value !== 'string') {
    return REDACTED;
  }

  // If value is too short, redact completely
  const minLength = showChars * 2 + 3; // Need room for both ends plus "..."
  if (value.length < minLength) {
    return REDACTED;
  }

  const start = value.slice(0, showChars);
  const end = value.slice(-showChars);
  return `${start}...${end}`;
}

/**
 * Checks if a key name represents sensitive data that should be redacted
 *
 * @param key - The key name to check
 * @returns True if the key contains sensitive data
 */
export function isSensitiveKey(key: string): boolean {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some(
    (sensitiveKey) =>
      lowerKey === sensitiveKey.toLowerCase() || lowerKey.includes(sensitiveKey.toLowerCase())
  );
}

/**
 * Redacts sensitive values from an object (deep clone)
 *
 * @param obj - The object to redact
 * @param options - Redaction options
 * @returns A new object with sensitive values redacted
 *
 * @example
 * redactSecrets({ apiKey: 'secret123', name: 'test' })
 * // { apiKey: '[REDACTED]', name: 'test' }
 *
 * redactSecrets({ apiKey: 'sk-1234567890' }, { mask: true })
 * // { apiKey: 'sk-1...7890', name: 'test' }
 */
export function redactSecrets<T extends Record<string, unknown>>(
  obj: T,
  options: { mask?: boolean; showChars?: number } = {}
): T {
  const { mask = false, showChars = 4 } = options;

  if (obj === null || obj === undefined) {
    return obj;
  }

  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) =>
      typeof item === 'object' && item !== null
        ? redactSecrets(item as Record<string, unknown>, options)
        : item
    ) as unknown as T;
  }

  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      if (typeof value === 'string') {
        result[key] = mask ? maskSecret(value, showChars) : REDACTED;
      } else {
        result[key] = REDACTED;
      }
    } else if (typeof value === 'object' && value !== null) {
      result[key] = redactSecrets(value as Record<string, unknown>, options);
    } else {
      result[key] = value;
    }
  }

  return result as T;
}

/**
 * Creates a safe log message by redacting any sensitive values
 *
 * @param message - The log message
 * @param data - Optional data object to include
 * @returns A tuple of [message, redactedData] safe for logging
 *
 * @example
 * const [msg, data] = safeLog('Request made', { apiKey: 'secret', user: 'john' });
 * console.log(msg, data);
 * // 'Request made' { apiKey: '[REDACTED]', user: 'john' }
 */
export function safeLog(
  message: string,
  data?: Record<string, unknown>
): [string, Record<string, unknown> | undefined] {
  if (!data) {
    return [message, undefined];
  }
  return [message, redactSecrets(data)];
}

/**
 * Wraps a logging function to automatically redact sensitive data
 *
 * @param logFn - The original logging function
 * @returns A wrapped function that redacts sensitive data
 *
 * @example
 * const safeDebug = createSafeLogger(console.debug);
 * safeDebug('API call', { apiKey: 'secret123' });
 * // Logs: 'API call' { apiKey: '[REDACTED]' }
 */
export function createSafeLogger(
  logFn: (...args: unknown[]) => void
): (...args: unknown[]) => void {
  return (...args: unknown[]) => {
    const safeArgs = args.map((arg) => {
      if (typeof arg === 'object' && arg !== null) {
        return redactSecrets(arg as Record<string, unknown>);
      }
      return arg;
    });
    logFn(...safeArgs);
  };
}
